import { HospitalityMenu } from "@/lib/types";
import { getHospitalityMenuDetails, updateHospitalityMenu } from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormSchemaType, HospitalityMenuMetaForm } from "../../form/HospitalityMenuMetaForm";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { useTranslation } from "react-i18next";
import { showApiError, showSuccess } from "@/lib/toast";
import { HospitalityMenuMetaFormImage } from "../../form/HospitalityMenuMetaFormImage";
import { LoadingSmall } from "@/components/LoadingSmall";

interface Props {
  hospitalityMenu: HospitalityMenu;
}

export const UpdateHospitalityMenuMetaForm: React.FC<Props> = ({ hospitalityMenu }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const { data: hospitalityMenuDetails, isLoading } = useQuery({
    queryKey: ["getHospitalityMenuDetails", hospitalityMenu.id],
    queryFn: () => getHospitalityMenuDetails(hospitalityMenu.id),
  });

  const queryClient = useQueryClient();
  const updateHospitalityMenuMutation = useMutation({
    mutationFn: (values: FormSchemaType) =>
      updateHospitalityMenu({
        ...values,
        id: hospitalityMenu.id,
        firmGuid: String(activeFirm?.firmGuid),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getHospitalityMenu"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getHospitalityMenuDetails"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full p-16">
        <LoadingSmall />
      </div>
    );
  }

  const hospitalityMenuDetailsData = hospitalityMenuDetails?.data;
  if (!hospitalityMenuDetailsData) {
    return <div>No hospitality menu details found</div>;
  }

  return (
    <>
      <HospitalityMenuMetaFormImage hospitalityMenu={hospitalityMenu} />
      <HospitalityMenuMetaForm
        buttonText={t("common.update")}
        onSubmit={(values) => updateHospitalityMenuMutation.mutate(values)}
        isLoading={updateHospitalityMenuMutation.isPending}
        defaultValues={{
          name: hospitalityMenuDetailsData.name,
          currencyId: hospitalityMenuDetailsData.currency_Id || undefined,
          pushId: String(hospitalityMenuDetailsData.pushId),
          autoSyncOn: hospitalityMenuDetailsData.autoSyncOn,
          autoSyncPeriodMinute: hospitalityMenuDetailsData.autoSyncPeriodMinute
            ? String(hospitalityMenuDetailsData.autoSyncPeriodMinute)
            : undefined,
        }}
      />
    </>
  );
};
